export const validateKeywords = (keywords) => {
  let validations = [];

  keywords.forEach((kw) => {
    const brackets = [...kw.matchAll(/[!?]\[(.*?)\]/g)];
    const numberBrackets = [...kw.matchAll(/[*&]\[(.*?)\]/g)];
    const numberInsideBracket = kw.match(/\[(0|[1-9]\d{0,4})\]/);
    const emptyKeywords = [...kw.matchAll(/[*&]\[(.*?)\]([^])/g)];

    const items = getItems(kw);
    const bracketsContent = getBracketContent(brackets);

    const itemsLength = items ? items.every((i) => i.length > 1 || i.length === 0) : true;
    const bracketsLength = bracketsContent ? bracketsContent.every((b) => b.length > 1 || b.length === 0) : true;

    let validation = {
      kw: kw,
      valid: true,
      endsRule: true,
      asteriskTildRule: true,
      balancedRule: true,
      bracketRule: true,
      punctuationRule: true,
      lengthRule: true,
      numberInsideBracketRule: true,
      emptyKeywordAfterBracketRule: true,
      bracketEmptyValid: true,
      numberInsideSpecialBracketRule: true,
    };

    validation.lengthRule = itemsLength && bracketsLength;
    validation.endsRule = !kw.endsWith('*') && !kw.startsWith('*') && !kw.endsWith('&') && !kw.startsWith('&');

    if (kw.includes('[')) {
      const bracketContent = kw.match(/.\[(.+?)\]/);
      const capturePrecedingSymbol = [...kw.matchAll(/(.)\[./g)];
      validation.bracketRule = !capturePrecedingSymbol.some(
        (bracket) => bracket[1] !== '*' && bracket[1] !== '&' && bracket[1] !== '?' && bracket[1] !== '!',
      );
      validation.bracketEmptyValid = !(!bracketContent && kw.includes(']')) && !kw.includes('[]');
      if (kw.includes('![') || kw.includes('?[')) {
        let isNumber = false;
        brackets.forEach((bracket) => {
          if (!isNaN(bracket[1])) {
            validation.numberInsideSpecialBracketRule = false;
            isNumber = true;
          }
        });
      }
      if (kw.includes('*[') || kw.includes('&[')) {
        let notNumber = false;
        numberBrackets.forEach((bracket) => {
          if (isNaN(bracket[1]) || bracket[1] < 0 || bracket[1] > 99999) {
            validation.numberInsideBracketRule = false;
            notNumber = true;
          }
        });
        if (!notNumber) {
          validation.numberInsideBracketRule = !!numberInsideBracket;
        }
      }
      if (emptyKeywords.length !== numberBrackets.length) {
        validation.emptyKeywordAfterBracketRule = false;
      }
    }
    validation.bracketRule = !(
      kw.includes('[') && !(kw.includes('![') || kw.includes('?[') || kw.includes('*[') || kw.includes('&['))
    );
    validation.asteriskTildRule = !kw.includes('*~');
    validation.punctuationRule = isPunctuationBalanced(kw, brackets);
    validation.balancedRule = isBalanced(kw);
    validation.valid =
      validation.endsRule &&
      validation.asteriskTildRule &&
      validation.balancedRule &&
      validation.bracketRule &&
      validation.punctuationRule &&
      validation.lengthRule &&
      validation.numberInsideSpecialBracketRule &&
      validation.numberInsideBracketRule &&
      validation.bracketEmptyValid &&
      validation.emptyKeywordAfterBracketRule;

    validations.push(validation);
  });

  return {
    ends: validations.every((kw) => kw.endsRule),
    asteriskTild: validations.every((kw) => kw.asteriskTildRule),
    balanced: validations.every((kw) => kw.balancedRule),
    bracket: validations.every((kw) => kw.bracketRule),
    punctuation: validations.every((kw) => kw.punctuationRule),
    length: validations.every((kw) => kw.lengthRule),
    numberInsideBracket: validations.every((kw) => kw.numberInsideBracketRule),
    numberInsideSpecialBracket: validations.every((kw) => kw.numberInsideSpecialBracketRule),
    bracketEmpty: validations.every((kw) => kw.bracketEmptyValid),
    emptyKeywordAfterBracket: validations.every((kw) => kw.emptyKeywordAfterBracketRule),
    valid: validations.every((kw) => kw.valid),
  };
};

const isBalanced = (input) => {
  let stack = [];

  // Traversing the Expression
  for (let i = 0; i < input.length; i++) {
    let x = input[i];

    if (x == '(' || x == '[' || x == '{') {
      // Push the element in the stack
      stack.push(x);
      continue;
    }

    // If current character is not opening
    // bracket, then it must be closing.
    // So stack cannot be empty at this point.
    // if (stack.length == 0) return false;

    let check;
    switch (x) {
      case ')':
        check = stack.pop();
        if (check == '{' || check == '[') return false;
        break;

      case '}':
        check = stack.pop();
        if (check == '(' || check == '[') return false;
        break;

      case ']':
        check = stack.pop();
        if (check == '(' || check == '{') return false;
        break;
    }
  }

  // Check Empty Stack
  return stack.length == 0;
};

const isPunctuationBalanced = (input, brackets) => {
  for (let i = 0; i < brackets.length; i++) {
    input = input.replace(brackets[i][0], '');
  }

  return input.match(/[!?]/g) ? false : true;
};

const getItems = (kw) => {
  return kw.split(/\*|~|&|\]|([!?]\[(.*?)\])/g).filter((x) => x !== undefined && x !== '');
};

const getBracketContent = (brackets) => {
  let content = [];
  brackets.forEach((bracket) => content.push(bracket[1].split(/[*|~]/g)));
  return content[0];
};

export const buildKeywords = (keywords) => {
  const kws = keywords
    .split(/\r\n|\r|\n/)
    .map((kw) => kw.trim())
    .filter(Boolean);

  return [...new Set(kws)];
};

// The keywords to add from text typed one per line: each once, without those already there. Keywords differing only
// by case are the same keyword.
export const keywordsToAdd = (rawKeywords, existingValues = []) => {
  const existing = new Set(existingValues.map((value) => value.toLowerCase()));
  const typed = new Map();
  buildKeywords(rawKeywords).forEach((keyword) => {
    const key = keyword.toLowerCase();
    if (!typed.has(key)) typed.set(key, keyword);
  });
  const keywords = [...typed].filter(([key]) => !existing.has(key)).map(([, keyword]) => keyword);
  return { keywords, alreadyThere: typed.size - keywords.length };
};

export const normalizeString = (string) => {
  const normalizedString = string.normalize('NFD');
  const cleanedString = normalizedString
    .replace(/[\u0300-\u036f\u2000-\u206F\u2E00-\u2E7F\\!"#$%&()*+,\-.\/:;<=>?@\[\]^_`{|}~]/g, '')
    .replace(/.*?'(.*)/, '$1');
  return cleanedString.toLowerCase();
};

export const mapKeywordPerDocument = (document) => {
  return Object.entries(document).map(([key, value]) => ({
    id: key,
    ...value,
  }));
};

export const sortingIndex = (obj1, obj2) => {
  const sortingFunction = (a, b) => {
    const idA = obj1.findIndex((item) => item.id === a);
    const idB = obj1.findIndex((item) => item.id === b);
    return idA - idB;
  };

  const sortedKeys = Object.keys(obj2).sort(sortingFunction);
  return sortedKeys.map((docId) => ({ docId: obj2[docId].docId, pages: obj2[docId].pages }));
};
